import classNames from 'classnames';

export const BUTTON_THEMES = ['main', 'danger', 'loud'] as const;
export const BUTTON_SIZES = ['large', 'medium', 'small', 'mini'] as const;
export const BUTTON_VARIANTS = ['filled', 'outlined', 'muted', 'transparent', 'loud', 'danger', 'danger-muted', 'light', 'dark'] as const;

export type ButtonSize = typeof BUTTON_SIZES[number];
export type ButtonTheme = typeof BUTTON_THEMES[number];
export type ButtonVariant = typeof BUTTON_VARIANTS[number];

export interface ButtonStyling {
  /**
   * Button style variants to help create visual hierarchy.
   *
   * See [button variant descriptions](https://cadence.reverb.tools/design-system/components/button/guidelines/#variant)
   * and [button usage guidelines](https://cadence.reverb.tools/design-system/components/button/guidelines/#variant)
   * for more information.
   */
  variant?: ButtonVariant;

  /**
   * The size of the button. Use `small` and `mini` sparingly.
   */
  size?: ButtonSize;

  /** Sets `width: 100%` in CSS so the button always fills its available space. */
  fullWidth?: boolean;

  /** Optimizes padding for a leading (before-text) icon. */
  hasLeadingIcon?: boolean;

  /** Optimizes padding for a trailing (after-text) icon. */
  hasTrailingIcon?: boolean;

  /** Sizes the button for an icon with no text. */
  iconOnly?: boolean;

  /** Collapses to icon-only at the given breakpoint. */
  iconOnlyAt?: 'mobile' | 'tablet';
}

export const DEFAULT_SIZE: ButtonSize = 'medium';
export const DEFAULT_VARIANT: ButtonVariant = 'muted';

/**
 * Function to generate className string to style any HTML element like an RCButton.
 * This is mainly for use on CoreLinks.
 *
 * @example
 * ```
 * <CoreLink
 *   to={something}
 *   className={buttonClassName({ variant: 'filled' })}
 * >
 *   I look like a button
 * </CoreLink>
 * ```
 */
export function buttonClassName({
  variant = DEFAULT_VARIANT,
  size = DEFAULT_SIZE,
  fullWidth = false,
  hasLeadingIcon = false,
  hasTrailingIcon = false,
  iconOnly = false,
  iconOnlyAt = undefined,
}: ButtonStyling): string {
  return classNames(
    'rc-button',
    {
      // color and state modifiers
      'rc-button--filled': variant === 'filled',
      'rc-button--outlined': variant === 'outlined',
      'rc-button--muted': variant === 'muted',
      'rc-button--transparent': variant === 'transparent',
      'rc-button--light': variant === 'light',
      'rc-button--dark': variant === 'dark',
      'rc-button--loud': variant === 'loud',
      'rc-button--danger': variant === 'danger',
      'rc-button--danger-muted': variant === 'danger-muted',

      // size modifiers
      'rc-button--large': size === 'large',
      'rc-button--medium': size === 'medium',
      'rc-button--small': size === 'small',
      'rc-button--mini': size === 'mini',

      'rc-button--full-width': fullWidth,
      'rc-button--icon-before': hasLeadingIcon,
      'rc-button--icon-after': hasTrailingIcon,
      'rc-button--icon-only': iconOnly,
      'rc-button--icon-only-at-mobile': iconOnlyAt === 'mobile',
      'rc-button--icon-only-at-tablet': iconOnlyAt === 'tablet',
    },
  );
}
